import React from "react";
import { Dialog as ThemesDialog, VisuallyHidden } from "@radix-ui/themes";
import { ModalTitleBarShell } from "./ModalTitleBar";

/**
 * Colored title bar for Radix Dialog.Content. The band itself comes from
 * ModalTitleBarShell so the two title bars cannot drift apart again — this file
 * adds only what is Radix-specific: the Dialog.Title that Radix requires for
 * accessibility, and a Dialog.Close that closes via onOpenChange rather than a
 * callback. Use with a Dialog.Content whose padding is 0, followed by a
 * `.modal-body-padded` body:
 *
 *   <Dialog.Content style={{ padding: 0, overflow: 'hidden' }} className="max-w-md">
 *     <RadixModalHeader title="Edit X" />
 *     <div className="modal-body-padded is-scroll"> ...form + actions... </div>
 *   </Dialog.Content>
 *
 * `disabled` and `className` work here too. They did not before, so a modal moved
 * from ModalTitleBar to this component quietly lost its guard against being
 * closed mid-save.
 *
 * `dialog` — READ THIS BEFORE USING THIS COMPONENT IN A NEW FILE.
 * There are two copies of @radix-ui/react-dialog in the tree: the app depends on
 * ^1.1.13 directly, while @radix-ui/themes reaches its own nested 1.1.6 through
 * the `radix-ui` umbrella package. They are different modules, so they publish
 * different React contexts, and a Title/Close from one CANNOT see a Root from
 * the other — you get "`DialogTitle` must be used within `Dialog`" and the whole
 * modal drops to the ErrorBoundary.
 *
 * So: if your file does `import * as Dialog from "@radix-ui/react-dialog"`, you
 * MUST pass `dialog={Dialog}`. If it uses `Dialog` from "@radix-ui/themes", the
 * default is already right. This is why the CRM and message modals used to
 * hand-roll the band — it was not duplication for its own sake.
 *
 * `onClose` opts out of Dialog.Close in favour of an imperative callback. That is
 * not a stylistic choice: the CRM modals mount with
 * `onOpenChange={() => showModal({ visible: false })}` but their own handleClose
 * ALSO clears formMode, so routing their × through Dialog.Close would leave stale
 * form state behind. Any dialog whose close path does more than flip `open` must
 * pass onClose.
 */
export default function RadixModalHeader({ title, subtitle, icon, onClose, showClose = true, actions, dialog, variant = "accent", color, disabled = false, className = "" }) {
  const D = dialog || ThemesDialog;
  const closeButton = (
    <button
      type="button"
      className="modal-titlebar-close"
      aria-label="Close"
      disabled={disabled}
      onClick={onClose}
    >×</button>
  );
  return (
    <ModalTitleBarShell title={title} subtitle={subtitle} icon={icon} variant={variant} color={color} className={className}>
      {/* The visible heading lives in the shell; Radix still needs a Dialog.Title in
          the tree, so it goes in hidden rather than rendering the title twice. */}
      <VisuallyHidden><D.Title>{title}</D.Title></VisuallyHidden>
      {/* `actions` sits between the title and the ×, for the one header that
          carries a status pill (MessageSelected's selected-record count). */}
      <div className="flex items-center gap-3">
        {actions}
        {/* showClose={false} for dialogs that deliberately have no × and are
            dismissed only by a button in the body (the CRM error and duplicate
            dialogs). Adding one would be a behaviour change, not a re-skin. */}
        {!showClose ? null : onClose ? closeButton : <D.Close asChild>{closeButton}</D.Close>}
      </div>
    </ModalTitleBarShell>
  );
}
